import { meshesOf } from '../../../ft3d/engine/mesh-presentation.js';
import { floorAt, ceilingAt, type ShapeCoverVolume } from '../../../ft3d/engine/ft3d-shape-core.js';
import { encounterCreatureField, itemsCol } from './array-types';
import { creatureTokenGeometry, selectCreaturePicture, defaultPictureReaders } from './creature-token-geometry';

/** Fixed banked body bounds, in feet. Posed limbs and equipment cannot resize navigation each frame. */
export function physicalCreatureBounds(meshes: unknown): { width: number; depth: number; height: number } | null {
  if (!Array.isArray(meshes)) return null;
  const rank = (mesh: Record<string, unknown>): number => mesh.variant === 'ft3d_avatar' ? 0 : mesh.variant === 'ft3d_avatar_clothed' ? 2 : 1;
  const rows = meshes.filter((mesh): mesh is Record<string, unknown> => !!mesh && typeof mesh === 'object');
  for (const mesh of rows.sort((a, b) => rank(a) - rank(b))) {
    if ((mesh.role ?? 'world') !== 'world' || !['glb', 'gltf'].includes(String(mesh.kind))
      || !/^https:\/\/cdn\.fatetide\.com\/assets\/meshes\/[a-f0-9]{64}\.glb$/.test(String(mesh.url))
      || !/^[a-f0-9]{64}$/.test(String(mesh.sha256)) || !Number.isSafeInteger(Number(mesh.bytes)) || Number(mesh.bytes) <= 0) continue;
    const bounds = mesh.boundsM;
    if (!Array.isArray(bounds) || bounds.length !== 3 || !bounds.every(value => Number.isFinite(Number(value)) && Number(value) > 0)) continue;
    return { width: Number(bounds[0]) / .3048, depth: Number(bounds[2]) / .3048, height: Number(bounds[1]) / .3048 };
  }
  return null;
}

export function physicalCreatureBody(meshes: unknown): { diameter: number; height: number } | null {
  const bounds = physicalCreatureBounds(meshes);
  return bounds ? { diameter: Math.max(bounds.width, bounds.depth), height: bounds.height } : null;
}

type Row = Record<string, unknown>;
export type DrawnCreatureBody = { heightFt: number; source: 'mesh' | 'standee'; r: number } &
  ({ kind: 'circle' } | { kind: 'oblong'; halfW: number; halfD: number; facing: number });
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const withCatalog = (row: Row, catalog?: unknown): Row => !record(catalog) || row.catalog === catalog ? row
  : { ...row, catalog: record(row.catalog) ? { ...record(catalog), ...record(row.catalog) } : catalog };

/** The scene's active form owns its mesh; a portrait-only form never inherits the actor's avatar. */
export function creatureAppearanceRow(row: Row, catalog?: unknown): Row | null {
  const source = withCatalog(row, catalog);
  const form = defaultPictureReaders.statuses(source, 'target').find(status =>
    !defaultPictureReaders.paused(status) && String(itemsCol(status, 'item_type') ?? '').toLowerCase() === 'creature');
  const flag = Array.isArray(row.shapeshifted) ? row.shapeshifted[0] : row.shapeshifted;
  return Number(flag ?? 0) === 1 || form ? record(form?.status_arr) : row;
}

/** Fixed drawn dimensions. A scene supplies its complete resolved appearance across sparse updates.
 * Rotation and poses do not resize a mesh creature's circle. */
export function drawnCreatureBody(row: Row, options: { catalog?: unknown; scale?: number;
  resolvedAppearance?: { meshes: unknown; token: { widthFt: number; heightFt: number; baseFt: number } };
} = {}): DrawnCreatureBody {
  const source = withCatalog(row, options.catalog);
  const appearance = options.resolvedAppearance ? null : creatureAppearanceRow(source);
  const mesh = physicalCreatureBounds(options.resolvedAppearance
    ? options.resolvedAppearance.meshes : appearance ? meshesOf(appearance as never) : null);
  const scale = Number.isFinite(options.scale) ? Math.max(.05, Math.min(24, options.scale!)) : 1;
  if (mesh) return { kind: 'circle', r: Math.max(mesh.width, mesh.depth) * scale / 2, heightFt: mesh.height * scale, source: 'mesh' };
  const token = options.resolvedAppearance?.token
    ?? creatureTokenGeometry(source, selectCreaturePicture(source, { use_picture_thumbnail: false }));
  const r = token.widthFt * scale / 2;
  const heightFt = (Number.isFinite(token.heightFt) && token.heightFt > 0 ? Math.max(.25, token.heightFt) : token.widthFt) * scale;
  return token.widthFt > token.baseFt
    ? { kind: 'oblong', r, halfW: r, halfD: token.baseFt * scale / 2, facing: 0, heightFt, source: 'standee' }
    : { kind: 'circle', r, heightFt, source: 'standee' };
}

/** One feet band for cover, area occupancy and the scene's preview. */
export function drawnBodyVolume(body: DrawnCreatureBody, at: { x: number; y: number; z: number },
  shape: Parameters<typeof floorAt>[0] | null, lying: boolean, clipCeiling = true): ShapeCoverVolume & { r: number } {
  const zMin = Math.max(shape ? floorAt(shape, at.x, at.y) ?? 0 : 0, at.z);
  const height = lying ? Math.max(1, body.heightFt * .4) : body.heightFt;
  const ceiling = shape && clipCeiling ? ceilingAt(shape, at.x, at.y) : null;
  // A ceiling at/below the feet is support, not headroom above a roof-standing body.
  const zMax = ceiling === null || ceiling <= zMin ? zMin + height : Math.max(zMin + .5, Math.min(zMin + height, ceiling));
  const band = { x: at.x, y: at.y, r: body.r, zMin, zMax };
  return body.kind === 'oblong'
    ? { kind: 'oblong', ...band, halfW: body.halfW, halfD: body.halfD, facing: body.facing }
    : { kind: 'circle', ...band };
}

/** `legacyCentre`: a shapeless (derived one-box) encounter persists a creature's z_pos at its visual CENTRE; the scene draws it on the
 *  feet plane (encounter-creation-engine feetPlaneElevationFt: max(0, z − height / 2)), so the band is seated there too. */
export function drawnCreatureVolume(row: Row, shape: Parameters<typeof floorAt>[0] | null = null,
  options: { catalog?: unknown; clipCeiling?: boolean; legacyCentre?: boolean;
    position: (row: Row) => { x: number; y: number | null; z: number };
    hasStatus: (row: Row, ...ids: string[]) => boolean }): (ShapeCoverVolume & { r: number }) | undefined {
  if (!record(row.encounter_creatures)) return undefined;
  try {
    const body = drawnCreatureBody(row, options), at = options.position(row);
    const lying = Number(encounterCreatureField(row.encounter_creatures, 'encounter_creature_dead') ?? 0) === 1
      || options.hasStatus(row, 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA', '9FEE2E8A-9CF7-D74D-5D16-B86C2603A1FA');
    const z = options.legacyCentre ? Math.max(0, at.z - body.heightFt / 2) : at.z;
    const volume = drawnBodyVolume(body, { x: at.x, y: at.y ?? 0, z }, shape, lying, options.clipCeiling);
    return [volume.x, volume.y, volume.r, volume.zMin, volume.zMax].every(Number.isFinite) && volume.r > 0 && body.heightFt > 0 ? volume : undefined;
  } catch { return undefined; }
}

/** A rigid body cannot use an aperture shorter than its physical height. */
export function physicalBodyNavigationShape<T extends { openings?: Array<{ passable?: boolean; z: {min: number; max: number} }> }>(shape: T, height: number): T {
  return { ...shape, openings: shape.openings?.map(opening => opening.z.max - opening.z.min + 1e-6 < height
    ? { ...opening, passable: false } : opening) };
}
